import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import { styled } from '@mui/material/styles';
import { useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { MuiThemeProvider } from '@/components/common/mui-theme-provider';
import {
  bindCardSelectionDisabled,
  CardsSelectionProvider,
  InternalContextProvider,
  type ISwimlaneProps,
  SwimlaneContextProvider,
  SwimlaneStateProvider,
  useExportMode,
  useSwimlaneRootProps,
} from './context';
import { SwimlaneAxisLabelLeft, SwimlaneAxisLabelTop } from './layout/axis/SwimlaneAxisLabel';
import { CardContextMenu } from './layout/body/cards/cardContextMenu';
import { TopLeftCorner } from './layout/body/TopLeftCorner';
import {
  AXIS_HEIGHT,
  COLUMN_HEADER_HEIGHT,
  GRID_GAP,
  GROUP_COL_INNER_GAP,
  ROW_HEADER_WIDTH,
} from './layout/constants';
import { ExportLayoutRoot } from './layout/ExportLayoutRoot';
import { AddHeader } from './layout/headers/AddHeader';
import { useCardEventHandlers } from './layout/hooks/useCardEventHandlers';
import { VirtualizerRoot } from './layout/VirtualizerRoot';

export type { ISwimlaneProps };

/**
 * Builds the CSS Grid template areas string based on configuration flags.
 * The grid structure is conditionally built based on:
 * - hasAxisLabelColumn: Whether a column axis label is present
 * - hasAxisLabelRow: Whether a row axis label is present
 * - hasRowHeaders: Whether row headers are present
 * - isAddColumnHeaderButtonVisible: Whether the add column button should be visible
 * - isAddRowHeaderButtonVisible: Whether the add row button should be visible
 */
function buildGridTemplateAreas(config: {
  hasAxisLabelColumn: boolean;
  hasAxisLabelRow: boolean;
  hasRowHeaders: boolean;
  isAddColumnHeaderButtonVisible: boolean;
  isAddRowHeaderButtonVisible: boolean;
}): string {
  const {
    hasAxisLabelColumn,
    hasAxisLabelRow,
    hasRowHeaders,
    isAddColumnHeaderButtonVisible,
    isAddRowHeaderButtonVisible,
  } = config;

  // Leading columns sit before the `auto 1fr` content columns: the row-axis label and the
  // row-header column. Each track only exists when its content does — an absent leading column
  // reserves no track, so CSS grid adds no gap on the left edge (a `0px` track would still get
  // a gap on both sides and leave a dangling GRID_GAP of dead space).
  const leadingCells = (axisYCell: string, rowHeaderCell: string): string[] => {
    const cells: string[] = [];
    if (hasAxisLabelRow) cells.push(axisYCell);
    if (hasRowHeaders) cells.push(rowHeaderCell);
    return cells;
  };
  const row = (leading: string[], content: string[]): string =>
    `"${[...leading, ...content].join(' ')}"`;

  const rows: string[] = [];

  // Row 1: Axis label row (if present)
  if (hasAxisLabelColumn) {
    rows.push(row(leadingCells('erase', 'erase'), ['axisX', 'axisX']));
  }

  // Row 2: Column headers row. When the add-column button is hidden, colH spans both columns.
  rows.push(
    row(
      leadingCells('erase', 'erase'),
      isAddColumnHeaderButtonVisible ? ['colH', 'addCol'] : ['colH', 'colH'],
    ),
  );

  // Row 3: Main grid content and row headers
  rows.push(row(leadingCells('axisY', 'rowH'), ['grid', 'grid']));

  // Row 4: Add row button row. The addRow cell only exists when the row-header column does.
  rows.push(row(leadingCells('axisY', isAddRowHeaderButtonVisible ? 'addRow' : '.'), ['.', '.']));

  return rows.join('\n');
}

export const Swimlane = (props: ISwimlaneProps) => (
  <MuiThemeProvider>
    <SwimlaneContextProvider {...props}>
      <SwimlaneBase data-dd-action-name="swimlane - general" />
    </SwimlaneContextProvider>
  </MuiThemeProvider>
);

const SwimlaneBase = () => {
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const {
    rowHeadersWidth,
    isAddColumnHeaderButtonVisible,
    isAddRowHeaderButtonVisible,
    readOnly,
    maxSelectedCards,
    rowHeaders,
    columnAxisLabelText,
    rowAxisLabelText,
    rowHeaderOrientation,
    backgroundColor,
    cells,
    items,
  } = useSwimlaneRootProps();
  const { isPng, isSvg } = useExportMode();
  const useStaticExportLayout = isPng || isSvg;

  const hasGroupHeaders = rowHeaders.some((h) => 'isGroupHeader' in h && h.isGroupHeader === true);
  const subRowHeaderWidth =
    rowHeaderOrientation === 'vertical' ? ROW_HEADER_WIDTH.vertical.value : rowHeadersWidth;
  const rowHeaderContainerWidth = hasGroupHeaders
    ? ROW_HEADER_WIDTH.vertical.value + GROUP_COL_INNER_GAP.value + subRowHeaderWidth
    : subRowHeaderWidth;
  const hasRowHeaders = rowHeaders.length > 0;
  const hasAxisLabelColumn = typeof columnAxisLabelText === 'string';
  const hasAxisLabelRow = typeof rowAxisLabelText === 'string';

  const cardEventDelegationProps = useCardEventHandlers();

  const colAxisLabelRef = useRef<HTMLDivElement>(null);
  const rowAxisLabelRef = useRef<HTMLDivElement>(null);

  return (
    <AutoSizer>
      {({ height, width }: { height: number; width: number }) => {
        // Compute grid template from configuration flags (axis labels, headers, add buttons)
        const gridTemplateRows = hasAxisLabelColumn
          ? `${AXIS_HEIGHT.label} ${COLUMN_HEADER_HEIGHT.label} auto 1fr`
          : `${COLUMN_HEADER_HEIGHT.label} auto 1fr`;

        // Mirror buildGridTemplateAreas: only emit a leading track when its column exists, so an
        // absent row-header/axis column leaves no dangling grid gap on the left edge.
        const leadingColumnWidths: string[] = [];
        if (hasAxisLabelRow) leadingColumnWidths.push(AXIS_HEIGHT.label);
        if (hasRowHeaders) leadingColumnWidths.push(`${rowHeaderContainerWidth}px`);
        const gridTemplateColumns = [...leadingColumnWidths, 'auto', '1fr'].join(' ');

        // Build grid template areas from configuration
        const gridTemplateAreas = buildGridTemplateAreas({
          hasAxisLabelColumn,
          hasAxisLabelRow,
          hasRowHeaders,
          isAddColumnHeaderButtonVisible,
          isAddRowHeaderButtonVisible,
        });

        return (
          // The Swimlane base grid is conditionally sized based on axis labels and hidden headers
          // Modifications to the grid must be done with caution!
          // Verify the behavior of: Top left corner, axis labels, and add headers. Each during resizing and scrolling.
          // Also verify the behavior when resizing horizontal row headers
          <StyledScopedCssBaseline
            ref={(node) => setScrollContainer(node)}
            backgroundColor={backgroundColor}
            style={{
              height,
              width,
              backgroundColor,
              gridGap: GRID_GAP.value,
              gridTemplateColumns,
              gridTemplateRows,
              gridTemplateAreas,
            }}
            {...cardEventDelegationProps}
            {...bindCardSelectionDisabled()}
            data-publicationid="swimlane-main-scrollable-container"
          >
            <SwimlaneStateProvider cells={cells} items={items}>
              {(hasAxisLabelRow || hasRowHeaders) && <TopLeftCorner />}
              {hasAxisLabelColumn && <SwimlaneAxisLabelTop ref={colAxisLabelRef} />}
              {hasAxisLabelRow && <SwimlaneAxisLabelLeft ref={rowAxisLabelRef} />}
              {isAddColumnHeaderButtonVisible && <AddHeader level={1} orientation="column" />}
              {hasRowHeaders && isAddRowHeaderButtonVisible && (
                <AddHeader level={2} orientation="row" />
              )}
              <InternalContextProvider data={{ colAxisLabelRef, rowAxisLabelRef }}>
                {scrollContainer && (
                  <CardsSelectionProvider
                    container={scrollContainer}
                    readOnly={readOnly}
                    maxSelectedCards={maxSelectedCards}
                  >
                    {useStaticExportLayout ? (
                      <ExportLayoutRoot scrollContainer={scrollContainer} />
                    ) : (
                      <VirtualizerRoot scrollContainer={scrollContainer} />
                    )}
                  </CardsSelectionProvider>
                )}
              </InternalContextProvider>
              <CardContextMenu />
            </SwimlaneStateProvider>
          </StyledScopedCssBaseline>
        );
      }}
    </AutoSizer>
  );
};

const StyledScopedCssBaseline = styled(ScopedCssBaseline, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor?: string }>(({ theme, backgroundColor }) => ({
  display: 'grid',
  overflow: 'auto',
  fontFamily: theme.typography.fontFamily,
  // Override MUI ScopedCssBaseline's default white background
  backgroundColor: backgroundColor || theme.palette.background.default,
}));
